import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Eye, EyeOff, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/tableau-de-bord';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      <div className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-zinc-900 mb-2"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('login.title')}
            </h1>
            <p className="text-zinc-600">
              {t('login.subtitle')}
            </p>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-8" data-testid="login-form">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="login-error">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-zinc-700">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.email_placeholder')}
                  required
                  className="mt-2 border-zinc-200 focus:border-blue-600 focus:ring-blue-600"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-700">{t('login.password')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="border-zinc-200 focus:border-blue-600 focus:ring-blue-600 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-3"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin inline" />{t('login.loading')}</>
                ) : t('login.submit')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-zinc-600">
              {t('login.no_account')}{' '}
              <Link to="/inscription" className="text-blue-600 hover:underline font-medium" data-testid="register-link">
                {t('login.sign_up')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginPage;
